/**
 * PublishWorkflow — the NA-190 lifecycle UI: action cluster, lifecycle
 * banners, and every confirmation/warning modal, with copy from the
 * publish-export-workflow spec's copy system. One component serves both
 * editors; behavior branches on contentKind and the active account context
 * (null = Emtrain/base).
 *
 * Action hierarchy per state (UI spec):
 *   Draft              → Publish (primary); Export hidden
 *   Published — locked → Export SCORM .zip (primary); Edit published content
 *                        lives in the lock banner
 *   Published — editing→ Export (with warning); Publish update lives in the
 *                        editing banner
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { designTokens } from '@course-authoring/shared-types';
import {
  BASE_ACCOUNT_SLUG,
  buildPublishDeployTargets,
  cancelEditContent,
  downloadScormExport,
  publishContent,
  publishUpdateContent,
  unlockContent,
  PublishApiError,
  type PublishableContentKind,
  type PublishState,
} from '../api/publishApi';
import {
  type DeployAccountResult,
} from '../api/courseDeployApi';
import { deliveryStatusQueryKey, pollDeliveryUntilRun, useDeliveryStatus } from '../api/deliveryStatusApi';
import { useScormExportContext } from '../api/accountAssignmentApi';
import { runPublishDelivery } from '../api/publishDelivery';
import { checkCurrentAuthoringBuild } from '../api/currentAuthoringBuild';
import { useAuth } from '../auth';
import { CdnLiveStatus } from './CdnLiveStatus';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';
import { formatDateTime } from '../utils/formatDateTime';
import {
  consumePublishResumeIntent,
  reloadForCurrentAuthoringBuild,
  storePublishResumeIntent,
  type PublishResumeOperation,
} from '../utils/publishResumeIntent';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PublishClientContext {
  /** hoot externalAccountId of the active client account. */
  accountId: number;
  accountName: string;
  /** DNS slug (subdomain); derived from the scorm-context prefill when absent. */
  accountSlug?: string;
}

export interface PublishActionClusterProps {
  contentKind: PublishableContentKind;
  /** Encoded public id — deploy path segment and export id. */
  packageId: string;
  /**
   * Raw auto-increment id behind the "C{n}"/"L{n}" badge. Names the export zip,
   * so the file an author downloads carries the id they can see in the UI.
   */
  numericId?: number;
  /** Content title — named in the SCORM wrapper manifest. */
  title: string;
  publishState: PublishState | null;
  /** Active client context, or null for the Emtrain (Standard) context. */
  clientContext?: PublishClientContext | null;
  disabled?: boolean;
  /**
   * SCORM sequential-lesson locking for deploys/exports. Independent of the
   * timed-course setting; no UI sets it yet, so callers pass false. When the
   * SCORM export flow gains an "enforce sequence" option, its value feeds
   * this prop — the downstream plumbing (deploy targets, launch-URL param,
   * player locking) is already wired.
   */
  enforceSequence?: boolean;
  /** Names failing/mid-edit lessons in the validation and release panels. */
  lessonTitlesById?: Record<string, string>;
  /** Persists pending editor changes before publish/update/deploy. */
  onBeforeAction: () => Promise<void>;
  /** "Review lessons" navigation from the validation panel. */
  onNavigateToLesson?: (lessonId: string) => void;
  /** Fired after any lifecycle transition so the page can refresh its state. */
  onPublishStateChange?: (state: PublishState | null) => void;
  /** Lets page-level banners open the unlock / publish-update modals. */
  onRegisterActions?: (actions: PublishClusterActions) => void;
}

export interface PublishClusterActions {
  openUnlock: () => void;
  openPublishUpdate: () => void;
  /** NA-296: no confirmation modal — cancel is a non-destructive rollback. */
  cancelEdit: () => void;
}


// Server-refused-transition codes: the UI's picture of the lifecycle was
// stale. Resync instead of leaving the author stuck against a 409.
const STATE_CONFLICT_CODES: ReadonlySet<string> = new Set([
  'ALREADY_PUBLISHED', 'NOT_PUBLISHED', 'ALREADY_EDITING', 'NOT_EDITING',
  'EDITS_ALREADY_SAVED', 'CONTENT_LOCKED', 'LOCKED_BY_EMTRAIN_EDITING',
]);

type ModalKind =
  | null
  | 'publish-confirm'
  | 'publish-validation'
  | 'publish-gate'
  | 'unlock-confirm'
  | 'update-confirm'
  | 'export-warning'
  | 'export-confirm'
  | 'deploy-results'
  | 'new-version';

/** Per-lesson outcome of the publish-gate consent action. */
type GateLessonOutcome =
  | { status: 'published' }
  | { status: 'failed'; reason: string };

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  cluster: { display: 'inline-flex', alignItems: 'center', gap: '12px' },
  primaryButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: designTokens.colors.selectionBorder,
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '8px 16px',
    borderRadius: '6px',
    border: `1px solid ${'#d1d5db' /* Gray-300 — no border token yet (CONV-6) */}`,
    backgroundColor: designTokens.colors.inspectorBackground,
    color: designTokens.colors.textPrimary,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    width: 'min(480px, calc(100vw - 48px))',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: designTokens.colors.inspectorBackground,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
  },
  // One shared scroll region for everything under the title — stacked result
  // lists can no longer push the actions past the viewport.
  modalScroll: { overflowY: 'auto', minHeight: 0 },
  modalTitle: { margin: 0, fontSize: '18px', fontWeight: 700, color: designTokens.colors.textPrimary },
  modalBody: { margin: '12px 0 0', fontSize: '14px', color: designTokens.colors.textPrimary, lineHeight: 1.5 },
  modalNote: { margin: '10px 0 0', fontSize: '13px', color: designTokens.colors.textSecondary, lineHeight: 1.5 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' },
  lessonList: { margin: '10px 0 0', paddingLeft: '18px', fontSize: '14px', lineHeight: 1.7 },
  lessonLink: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: designTokens.colors.selectionBorder,
    cursor: 'pointer',
    fontSize: '14px',
    textDecoration: 'underline',
  },
  resultRow: { display: 'flex', gap: '8px', fontSize: '13px', margin: '6px 0', alignItems: 'baseline' },
  toast: {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: designTokens.colors.textPrimary,
    color: '#ffffff',
    padding: '10px 18px',
    borderRadius: '8px',
    fontSize: '14px',
    zIndex: 1100,
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.3)',
  },
};

// ─── Banners ─────────────────────────────────────────────────────────────────

const bannerStyles: Record<string, CSSProperties> = {
  lock: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #d1d5db', // Gray-300 — no border token yet (CONV-6)
    backgroundColor: '#f3f4f6', // Gray-100
    margin: '0 0 16px',
  },
  editing: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    border: `1px solid ${designTokens.colors.workflowDraft}`,
    backgroundColor: '#fef3c7', // Amber-100
    margin: '0 0 16px',
  },
  title: { margin: 0, fontSize: '14px', fontWeight: 700, color: designTokens.colors.textPrimary },
  body: { margin: '2px 0 0', fontSize: '13px', color: designTokens.colors.textSecondary, lineHeight: 1.4 },
  statusLine: { display: 'flex', alignItems: 'center', gap: '10px' },
  timestamp: { fontSize: '13px', color: designTokens.colors.textPrimary },
};

/**
 * The Published — locked banner above the editable canvas. Carries the
 * lifecycle status (Published badge + last-published timestamp) and the
 * Edit published content action. When the Emtrain version is mid-edit
 * (NA-221), the lock message switches to the Emtrain-lock copy and the
 * unlock action disappears.
 */
/**
 * Inline verification is reserved for single-scope target sets (a client
 * context, or the Emtrain context with no assigned accounts). Beyond one
 * scope, publish surfaces summarize and link to the Delivery status page —
 * per-scope polling badges do not scale to hundreds of assigned accounts.
 */
export const INLINE_VERIFY_MAX_SCOPES = 1;

const deliveryPagePath = (contentKind: PublishableContentKind, packageId: string): string =>
  contentKind === 'course' ? `/courses/${packageId}/delivery` : `/lessons/${packageId}/delivery`;

/**
 * The compact fleet summary + page link shown when the Emtrain context has
 * assigned accounts. Reads the shared aggregate snapshot (one query key with
 * the modal summary and the Delivery status page — one poll stream total).
 */
function DeliverySummaryLine({
  contentKind,
  packageId,
  poll = false,
}: {
  contentKind: PublishableContentKind;
  packageId: string;
  /** Exactly ONE surface per tree polls: the banner's summary line owns the
   * aggregate interval (it exists only for multi-scope fleets); the modal's
   * instance and the banner's size observer ride the shared cache. */
  poll?: boolean;
}) {
  const status = useDeliveryStatus(contentKind, packageId, true, poll);
  const summary = status.data?.summary;
  const issues = summary
    ? summary.failed + summary.stale + summary.partial + summary.neverDeployed + summary.unknown
    : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '13px', flexWrap: 'wrap' }} role="status">
      {summary ? (
        <>
          <span style={{ color: designTokens.colors.workflowPublished, fontWeight: 600 }}>
            Latest publish delivered {summary.current}/{summary.total}
          </span>
          {summary.propagating > 0 && (
            <span style={{ color: designTokens.colors.workflowDraft, fontWeight: 600 }}>
              · preparing {summary.propagating}
            </span>
          )}
          {issues > 0 && (
            <span style={{ color: designTokens.colors.errorText, fontWeight: 600 }}>
              · {issues} {issues === 1 ? 'issue' : 'issues'}
            </span>
          )}
        </>
      ) : (
        <span style={{ color: designTokens.colors.workflowArchived }}>
          Checking delivery across account contexts…
        </span>
      )}
      <Link
        to={deliveryPagePath(contentKind, packageId)}
        style={{ color: designTokens.colors.selectionBorder, fontSize: '13px' }}
      >
        View delivery status
      </Link>
    </div>
  );
}

export function PublishLockBanner({
  contentKind,
  lockedByEmtrainEditing,
  lastPublishedAt,
  onUnlock,
  packageId,
  accountSlug,
}: {
  contentKind: PublishableContentKind;
  lockedByEmtrainEditing: boolean;
  lastPublishedAt?: string | null;
  onUnlock?: () => void;
  /** When set, the banner shows the CDN served-truth badge for this package. */
  packageId?: string;
  /** Scope for the CDN check; omitted = base (Emtrain) scope. */
  accountSlug?: string;
}) {
  // Single-scope rule for the banner: client contexts (accountSlug set) keep
  // the per-scope badge; the Emtrain context checks the fleet size and swaps
  // to the compact summary + page link once accounts are assigned.
  // The fleet-size observer NEVER polls: for a single-scope fleet the badge's
  // own 10s check is the only poller, and for multi-scope fleets the summary
  // line below is the designated aggregate polling owner.
  const fleetStatus = useDeliveryStatus(
    contentKind,
    packageId ?? null,
    accountSlug === undefined && Boolean(packageId) && !lockedByEmtrainEditing,
    false,
  );
  // Client contexts must never swap to the fleet summary — even when the
  // shared snapshot is already cached from another surface. While the fleet
  // size is UNKNOWN (loading or failed), the Emtrain context must not guess
  // single-scope: mounting the inline checker would auto-poll the base scope
  // for what may be a large fleet — exactly what the summary exists to avoid.
  const fleetKnown = accountSlug !== undefined || fleetStatus.data !== undefined;
  const fleetIsMultiScope =
    accountSlug === undefined && (fleetStatus.data?.summary.total ?? 0) > INLINE_VERIFY_MAX_SCOPES;

  if (lockedByEmtrainEditing) {
    const capitalized = contentKind === 'course' ? 'Course' : 'Lesson';
    return (
      <div style={bannerStyles.lock} role="status">
        <div>
          <p style={bannerStyles.title}>{capitalized} locked for editing while Emtrain updates the published content.</p>
          <p style={bannerStyles.body}>Editing will be available once the update is complete.</p>
        </div>
      </div>
    );
  }
  return (
    <div style={bannerStyles.lock} role="status">
      <div>
        <div style={bannerStyles.statusLine}>
          <WorkflowStatusBadge state="published" />
          <span style={bannerStyles.timestamp}>{formatDateTime(lastPublishedAt) ?? '—'}</span>
        </div>
        <p style={bannerStyles.body}>
          Published content is locked to protect the exportable version. To make changes,
          click “Edit published content”
        </p>
        {packageId && (fleetIsMultiScope ? (
          <DeliverySummaryLine contentKind={contentKind} packageId={packageId} poll />
        ) : !fleetKnown ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginTop: '6px', fontSize: '13px' }} role="status">
            <span style={{ color: designTokens.colors.workflowArchived }}>
              {fleetStatus.isError ? 'Delivery status unavailable.' : 'Checking delivery status…'}
            </span>
            <Link to={deliveryPagePath(contentKind, packageId)} style={{ fontSize: '13px' }}>
              View delivery status
            </Link>
          </span>
        ) : (
          <CdnLiveStatus
            packageId={packageId}
            accountSlug={accountSlug}
            pollToken={lastPublishedAt}
            publishedAt={lastPublishedAt}
          />
        ))}
      </div>
      {onUnlock && (
        <button type="button" style={styles.secondaryButton} onClick={onUnlock}>
          Edit published content
        </button>
      )}
    </div>
  );
}

/** The persistent Published — editing banner ("You have unpublished edits"). */
export function PublishEditingBanner({
  onPublishUpdate,
  onCancelEdit,
  canCancelEdit = false,
}: {
  onPublishUpdate?: () => void;
  /** NA-296: back out to Published — locked without publishing. */
  onCancelEdit?: () => void;
  /** Only true while no edit has been saved since unlock — see PublishState.canCancelEdit. */
  canCancelEdit?: boolean;
}) {
  return (
    <div style={bannerStyles.editing} role="status">
      <div>
        <p style={bannerStyles.title}>You have unpublished edits</p>
        <p style={bannerStyles.body}>
          Your changes are saved, but they are not included in the exportable SCORM package
          until you publish an update.
        </p>
      </div>
      <div style={{ display: 'inline-flex', gap: '8px' }}>
        {onCancelEdit && canCancelEdit && (
          <button type="button" style={styles.secondaryButton} onClick={onCancelEdit}>
            Cancel
          </button>
        )}
        {onPublishUpdate && (
          <button type="button" style={styles.primaryButton} onClick={onPublishUpdate}>
            Publish update
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Modal shell ─────────────────────────────────────────────────────────────

function WorkflowModal({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={styles.overlay} role="presentation">
      <div style={styles.modal} role="dialog" aria-modal="true" aria-label={title}>
        <h2 style={styles.modalTitle}>{title}</h2>
        <div style={styles.modalScroll}>{children}</div>
      </div>
    </div>
  );
}

// ─── Action cluster ──────────────────────────────────────────────────────────

export function PublishActionCluster({
  contentKind,
  packageId,
  numericId,
  title,
  publishState,
  clientContext = null,
  disabled = false,
  enforceSequence = false,
  lessonTitlesById = {},
  onBeforeAction,
  onNavigateToLesson,
  onPublishStateChange,
  onRegisterActions,
}: PublishActionClusterProps) {
  const { user } = useAuth();
  const location = useLocation();
  const [modal, setModal] = useState<ModalKind>(null);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState<string | null>(null);
  const [failedLessonIds, setFailedLessonIds] = useState<string[]>([]);
  // Never-published contained lessons blocking a course publish/update
  // (referenced-lesson-packages gate: the course pointer needs a published
  // package per lesson to reference).
  const [unpublishedLessonIds, setUnpublishedLessonIds] = useState<string[]>([]);
  // Which flow the gate interrupted, so the consent action can resume it.
  const [gateResume, setGateResume] = useState<'publish' | 'update'>('publish');
  const [gateOutcomes, setGateOutcomes] = useState<Record<string, GateLessonOutcome>>({});
  const [deployResults, setDeployResults] = useState<DeployAccountResult[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [staleBuildId, setStaleBuildId] = useState<string | null>(null);
  const [staleOperation, setStaleOperation] = useState<PublishResumeOperation | null>(null);
  // Post-publish acknowledgement polling must die with this surface — an
  // abandoned loop otherwise keeps fetching after navigation.
  const pollAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    pollAbortRef.current = controller;
    return () => controller.abort();
  }, []);

  // null = state unknown (loading/stale) — render no lifecycle actions.
  const state = publishState?.state ?? null;
  const isClientContext = clientContext !== null;
  const failedDeploys = deployResults.filter((result) => !result.success);
  // Published fine, but delivery is degraded (invalidation failed/skipped,
  // verification stamp or tracking missing) — must never render as a clean
  // success, that is exactly how a stale package hides behind a green dialog.
  const warningDeploys = deployResults.filter((result) => result.success && result.warning);
  // The scopes THIS publish deployed, with each deploy's identity — the
  // results modal verifies exactly these deploys (a later publish flips the
  // badge to "superseded" instead of certifying work this one didn't do).
  const [verifyTargets, setVerifyTargets] = useState<Array<{ accountSlug: string; deployId?: string }>>([]);
  // The INTENDED fan-out size of the last publish — the inline-vs-summary
  // threshold keys on the target set, never on how many scopes succeeded.
  const [deployScopeCount, setDeployScopeCount] = useState(0);
  const scormContext = useScormExportContext(contentKind, (state !== null && state !== 'draft') || modal !== null ? packageId : null);
  const queryClient = useQueryClient();
  // A lesson delivery changes what PARENT-COURSE snapshots report. The server
  // evicts its cache, but a settled course query has no interval and focus
  // refetch is off app-wide — a mounted viewer would never refetch. Invalidate
  // this session's course snapshots; other tabs converge on manual refresh
  // (documented limitation).
  const invalidateParentCourseSnapshots = useCallback(() => {
    if (contentKind !== 'lesson') return;
    void queryClient.invalidateQueries({ queryKey: ['deliveryStatus', 'course'] });
  }, [contentKind, queryClient]);

  const verifyCurrentBuild = useCallback(async (
    operation: PublishResumeOperation,
  ): Promise<boolean> => {
    const result = await checkCurrentAuthoringBuild();
    if (result.status === 'current') {
      return true;
    }
    if (result.status === 'stale') {
      setStaleBuildId(result.deployedBuildId);
      setStaleOperation(operation);
      setModal('new-version');
      return false;
    }

    setErrorMessage('Unable to verify the current AuthorAI version. Check your connection and try publishing again.');
    return false;
  }, []);


  // Export scope: the active client account's scorm-context entry carries the
  // slug (subdomain), credentials, and SCORM version the wrapper bakes in.
  const exportAccount = useMemo(() => {
    if (!clientContext) return null;
    return scormContext.data?.accounts.find(
      (candidate) => Number(candidate.externalId) === clientContext.accountId,
    ) ?? null;
  }, [clientContext, scormContext.data]);

  // ── Cancel (editing → locked, no publish) ──────────────────────────────────
  // NA-296: only ever called while canCancelEdit is true (no confirmation —
  // nothing is lost). A 409 here means a save landed between the last state
  // refresh and this click; resync rather than silently no-op.
  const executeCancelEdit = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const response = await cancelEditContent(contentKind, packageId, clientContext?.accountId ?? null);
      onPublishStateChange?.(response.publishState);
    } catch (error) {
      if (error instanceof PublishApiError && STATE_CONFLICT_CODES.has(error.code)) {
        setErrorMessage(`${error.message} Refreshing the publish status…`);
        onPublishStateChange?.(null);
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Cancel failed.');
      }
    } finally {
      setBusy(false);
    }
  }, [clientContext, contentKind, onPublishStateChange, packageId]);

  useEffect(() => {
    onRegisterActions?.({
      openUnlock: () => { setErrorMessage(null); setModal('unlock-confirm'); },
      openPublishUpdate: () => { setErrorMessage(null); setModal('update-confirm'); },
      cancelEdit: () => { void executeCancelEdit(); },
    });
  }, [onRegisterActions, executeCancelEdit]);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const lessonName = useCallback(
    (lessonId: string) => lessonTitlesById[lessonId] ?? lessonId,
    [lessonTitlesById],
  );

  // ── Deploy execution (publish + publish update) ──────────────────────────
  const runDeploys = useCallback(async (onlyAccountId?: number, contentPublishedAt?: string) => {
    await runPublishDelivery({
      contentKind, packageId, exportedBy: user?.id || 'unknown', contentPublishedAt,
      onlyAccountId, clientContext, enforceSequence,
      loadContext: async () => {
        const refreshed = await scormContext.refetch();
        return refreshed.isError ? undefined : refreshed.data;
      },
      onProgress: setBusyLabel,
      onResults: setDeployResults,
      onTargetCount: setDeployScopeCount,
      onVerifyTargets: setVerifyTargets,
      onSnapshotChanged: () => {
        void queryClient.invalidateQueries({ queryKey: deliveryStatusQueryKey(contentKind, packageId) });
      },
      onRunRecorded: (runId) => {
        void pollDeliveryUntilRun(queryClient, contentKind, packageId, runId, { signal: pollAbortRef.current?.signal });
      },
      onParentSnapshotsChanged: invalidateParentCourseSnapshots,
      describeLesson: lessonName,
    });
  }, [clientContext, contentKind, enforceSequence, lessonName, packageId, queryClient, scormContext, user]);

  // ── Publish (Draft → Published) ───────────────────────────────────────────
  const executePublish = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      setBusyLabel('Publishing…');
      await onBeforeAction();
      if (!await verifyCurrentBuild('publish')) return;
      const published = await publishContent(contentKind, packageId);
      await runDeploys(undefined, published.publishedAt);
      setModal('deploy-results');
      showToast(`${contentKind === 'course' ? 'Course' : 'Lesson'} published.`);
      onPublishStateChange?.(null);
    } catch (error) {
      if (error instanceof PublishApiError && error.code === 'MISSING_LESSON_COMPLETE') {
        setFailedLessonIds(error.lessonIds.length > 0 ? error.lessonIds : [packageId]);
        setModal('publish-validation');
      } else if (error instanceof PublishApiError && error.code === 'LESSONS_NOT_PUBLISHED') {
        // The course pointer references each lesson's published package, so a
        // never-published contained lesson blocks the publish (no cascade).
        setUnpublishedLessonIds(error.lessonIds);
        setGateResume('publish');
        setGateOutcomes({});
        setModal('publish-gate');
      } else if (error instanceof PublishApiError && STATE_CONFLICT_CODES.has(error.code)) {
        setErrorMessage(`${error.message} Refreshing the publish status…`);
        onPublishStateChange?.(null);
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Publish failed.');
      }
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  }, [contentKind, onBeforeAction, onPublishStateChange, packageId, runDeploys, showToast, verifyCurrentBuild]);

  // ── Unlock (Published — locked → editing) ─────────────────────────────────
  const handleConfirmUnlock = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const response = await unlockContent(contentKind, packageId, clientContext?.accountId ?? null);
      setModal(null);
      onPublishStateChange?.(response.publishState);
    } catch (error) {
      if (error instanceof PublishApiError && STATE_CONFLICT_CODES.has(error.code)) {
        setErrorMessage(`${error.message} Refreshing the publish status…`);
        onPublishStateChange?.(null);
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Unlock failed.');
      }
    } finally {
      setBusy(false);
    }
  }, [clientContext, contentKind, onPublishStateChange, packageId]);

  // ── Publish update (editing → locked) ─────────────────────────────────────
  const executePublishUpdate = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      setBusyLabel('Publishing update…');
      await onBeforeAction();
      if (!await verifyCurrentBuild('publish-update')) return;
      const response = await publishUpdateContent(contentKind, packageId, {
        accountId: clientContext?.accountId ?? null,
      });
      await runDeploys(
        clientContext?.accountId,
        response.publishState?.lastPublishedAt ?? response.publishedAt ?? undefined,
      );
      setModal('deploy-results');
      showToast('Update published. This course is locked and ready to export.');
      onPublishStateChange?.(response.publishState);
    } catch (error) {
      if (error instanceof PublishApiError && error.code === 'MISSING_LESSON_COMPLETE') {
        setFailedLessonIds(error.lessonIds.length > 0 ? error.lessonIds : [packageId]);
        setModal('publish-validation');
      } else if (error instanceof PublishApiError && error.code === 'LESSONS_NOT_PUBLISHED') {
        // Same gate as initial publish: a still-draft lesson added while the
        // course was unlocked must be published before the update ships.
        setUnpublishedLessonIds(error.lessonIds);
        setGateResume('update');
        setGateOutcomes({});
        setModal('publish-gate');
      } else if (error instanceof PublishApiError && STATE_CONFLICT_CODES.has(error.code)) {
        setErrorMessage(`${error.message} Refreshing the publish status…`);
        onPublishStateChange?.(null);
      } else {
        setErrorMessage(error instanceof Error ? error.message : 'Publish update failed.');
      }
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  }, [clientContext, contentKind, onBeforeAction, onPublishStateChange, packageId, runDeploys, showToast, verifyCurrentBuild]);

  const handleReloadAndContinue = useCallback(() => {
    if (!user || !staleBuildId || !staleOperation) return;

    const lifecycleState = staleOperation === 'publish' ? 'draft' : 'editing';
    storePublishResumeIntent({
      // AuthorAPI currently serializes its numeric primary key at runtime even
      // though the shared frontend User type declares a string. Canonicalize
      // at this persistence boundary so the post-reload validator sees the
      // same representation on both sides.
      userId: String(user.id),
      routePath: location.pathname,
      contentKind,
      contentId: packageId,
      operation: staleOperation,
      lifecycleState,
      accountId: clientContext?.accountId ?? null,
      observedBuildId: staleBuildId,
    });
    reloadForCurrentAuthoringBuild(staleBuildId);
  }, [clientContext, contentKind, location.pathname, packageId, staleBuildId, staleOperation, user]);

  useEffect(() => {
    if (!user || (state !== 'draft' && state !== 'editing')) return;

    const intent = consumePublishResumeIntent({
      userId: String(user.id),
      routePath: location.pathname,
      contentKind,
      contentId: packageId,
      lifecycleState: state,
      accountId: clientContext?.accountId ?? null,
    });
    if (!intent) return;
    // The editor can invalidate an otherwise matching intent while the page is
    // reloading (for example, a course can lose its final lesson). Preserve the
    // same availability guard as an explicit click and require a new action.
    if (disabled) return;

    if (intent.operation === 'publish') {
      setModal('publish-confirm');
      void executePublish();
    } else {
      setModal('update-confirm');
      void executePublishUpdate();
    }
  }, [
    clientContext,
    contentKind,
    disabled,
    executePublish,
    executePublishUpdate,
    location.pathname,
    packageId,
    state,
    user,
  ]);

  // ── Gate consent: publish the listed lessons, then resume the course flow ─
  // Explicit per-lesson publishes followed by a re-issued course publish/update
  // — the server gate is unchanged and re-evaluates. Failures don't abort the
  // remaining attempts (one pass reports every problem); a concurrent
  // ALREADY_PUBLISHED still satisfies the gate. Packages for the freshly
  // published lessons are built by the course deploy's existence/heal check.
  const executeGateLessonPublishes = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    const outcomes: Record<string, GateLessonOutcome> = {};
    let anyFailed = false;
    const total = unpublishedLessonIds.length;
    for (let index = 0; index < total; index += 1) {
      const lessonId = unpublishedLessonIds[index];
      if (lessonId === undefined) continue;
      setBusyLabel(`Publishing lesson ${index + 1} of ${total}…`);
      try {
        await publishContent('lesson', lessonId);
        outcomes[lessonId] = { status: 'published' };
      } catch (error) {
        if (error instanceof PublishApiError && error.code === 'ALREADY_PUBLISHED') {
          outcomes[lessonId] = { status: 'published' };
        } else if (error instanceof PublishApiError && error.code === 'MISSING_LESSON_COMPLETE') {
          anyFailed = true;
          outcomes[lessonId] = { status: 'failed', reason: 'Needs a Lesson Complete block before it can be published.' };
        } else {
          anyFailed = true;
          outcomes[lessonId] = { status: 'failed', reason: error instanceof Error ? error.message : 'Publish failed.' };
        }
      }
      setGateOutcomes({ ...outcomes });
    }
    setBusy(false);
    setBusyLabel(null);
    if (!anyFailed) {
      if (gateResume === 'update') {
        await executePublishUpdate();
      } else {
        await executePublish();
      }
    }
  }, [executePublish, executePublishUpdate, gateResume, unpublishedLessonIds]);

  // ── Export (thin SCORM wrapper pointing at the last-deployed package) ────
  const handleConfirmExport = useCallback(async () => {
    setBusy(true);
    setErrorMessage(null);
    try {
      const context = scormContext.data;
      if (!context) {
        throw new Error('Export prefill is still loading; try again in a moment.');
      }
      if (isClientContext && !exportAccount) {
        throw new Error('This client account has no export scope configured yet.');
      }
      setBusyLabel('Preparing SCORM .zip…');
      await downloadScormExport({
        packageId,
        numericId,
        title,
        context,
        account: exportAccount,
        enforceSequence,
      });
      setModal(null);
      showToast('SCORM package exported.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Export failed.');
    } finally {
      setBusy(false);
      setBusyLabel(null);
    }
  }, [enforceSequence, exportAccount, isClientContext, numericId, packageId, scormContext.data, showToast, title]);

  const openExport = useCallback(() => {
    setErrorMessage(null);
    setModal(state === 'editing' ? 'export-warning' : 'export-confirm');
  }, [state]);

  const closeModal = useCallback(() => {
    if (busy) return;
    setModal(null);
    setErrorMessage(null);
  }, [busy]);

  const contentNoun = contentKind === 'course' ? 'course' : 'lesson';

  // ── Buttons per state ─────────────────────────────────────────────────────
  const actions = useMemo(() => {
    if (state === null) return null;
    if (state === 'draft') {
      // Draft exists only in the Emtrain context (client contexts open draft
      // content in Preview, not the editor); Export is hidden entirely.
      return (
        <button
          type="button"
          style={styles.primaryButton}
          disabled={disabled || busy}
          onClick={() => { setErrorMessage(null); setModal('publish-confirm'); }}
        >
          {busyLabel ?? 'Publish'}
        </button>
      );
    }
    if (state === 'locked') {
      // Edit published content lives in the lock banner (wired back through
      // onRegisterActions), so the cluster offers only the primary Export.
      return (
        <button
          type="button"
          style={styles.primaryButton}
          disabled={disabled || busy}
          onClick={openExport}
        >
          {busyLabel ?? 'Export SCORM .zip'}
        </button>
      );
    }
    // Publish update is offered by the persistent editing banner, not the
    // cluster — one primary CTA on screen for the editing state.
    return (
      <button
        type="button"
        style={styles.secondaryButton}
        disabled={disabled || busy}
        onClick={openExport}
      >
        Export SCORM .zip
      </button>
    );
  }, [busy, busyLabel, contentKind, disabled, openExport, publishState, state]);

  return (
    <span style={styles.cluster}>
      {actions}

      {modal === 'new-version' && (
        <WorkflowModal title="A new version is available">
          <p style={styles.modalBody}>
            AuthorAI was updated while you were working. Reload the page to continue publishing. Your changes are saved.
          </p>
          <div style={styles.modalActions}>
            <button type="button" style={styles.primaryButton} onClick={handleReloadAndContinue}>
              Reload and continue
            </button>
          </div>
        </WorkflowModal>
      )}

      {modal === 'publish-confirm' && (
        <WorkflowModal title={`Publish ${contentNoun}?`}>
          <p style={styles.modalBody}>
            Publishing locks this {contentNoun} and makes it available for SCORM export.
          </p>
          <p style={styles.modalBody}>
            You will still be able to edit it later, but published content must be explicitly
            unlocked before changes can be made. Future edits will not be included in export
            until you publish an update.
          </p>
          <p style={styles.modalNote}>This action cannot be reverted to Draft.</p>
          {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
            <button type="button" style={styles.primaryButton} onClick={() => void executePublish()} disabled={busy}>
              {busy ? busyLabel ?? 'Publishing…' : `Publish ${contentNoun}`}
            </button>
          </div>
        </WorkflowModal>
      )}

      {modal === 'publish-validation' && (
        <WorkflowModal title={`${contentKind === 'course' ? 'Course' : 'Lesson'} can’t be published yet`}>
          <p style={styles.modalBody}>Each lesson must include a Lesson Complete block before publishing.</p>
          <p style={styles.modalNote}>Missing Lesson Complete block:</p>
          <ul style={styles.lessonList}>
            {failedLessonIds.map((lessonId) => (
              <li key={lessonId}>
                {onNavigateToLesson ? (
                  <button type="button" style={styles.lessonLink} onClick={() => { setModal(null); onNavigateToLesson(lessonId); }}>
                    {lessonName(lessonId)}
                  </button>
                ) : (
                  lessonName(lessonId)
                )}
              </li>
            ))}
          </ul>
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal}>Cancel</button>
            {onNavigateToLesson && failedLessonIds[0] !== undefined && (
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => { const first = failedLessonIds[0]; setModal(null); if (first) onNavigateToLesson(first); }}
              >
                Review lessons
              </button>
            )}
          </div>
        </WorkflowModal>
      )}

      {modal === 'unlock-confirm' && (
        isClientContext ? (
          // Client-context copy (NA-199): shown every time, regardless of
          // whether the lesson already has customized blocks — edits here
          // disconnect touched blocks from the Emtrain standard version.
          <WorkflowModal title={`Editing this ${contentNoun} for ${clientContext?.accountName}`}>
            <p style={styles.modalBody}>
              Changes you make here apply only to {clientContext?.accountName}. Any block you
              edit will be disconnected from the Emtrain standard version and won’t receive
              future content updates made by Emtrain. Blocks you don’t edit will continue to be
              synced with the standard version.
            </p>
            {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
            <div style={styles.modalActions}>
              <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
              <button type="button" style={styles.primaryButton} onClick={() => void handleConfirmUnlock()} disabled={busy}>
                Continue editing
              </button>
            </div>
          </WorkflowModal>
        ) : (
          <WorkflowModal title="Edit published content?">
            <p style={styles.modalBody}>This will unlock the {contentNoun} so you can make changes.</p>
            <p style={styles.modalBody}>
              Your edits will auto-save, but they will not be included in the exportable version
              until you publish an update.
            </p>
            <p style={styles.modalNote}>The current published version remains available for export while you edit.</p>
            {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
            <div style={styles.modalActions}>
              <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
              <button type="button" style={styles.primaryButton} onClick={() => void handleConfirmUnlock()} disabled={busy}>
                Unlock for editing
              </button>
            </div>
          </WorkflowModal>
        )
      )}

      {modal === 'update-confirm' && (
        <WorkflowModal title="Publish update?">
          <p style={styles.modalBody}>
            This will update the published version of the {contentNoun} and make the latest
            changes available for SCORM export.
          </p>
          {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
            <button type="button" style={styles.primaryButton} onClick={() => void executePublishUpdate()} disabled={busy}>
              {busy ? busyLabel ?? 'Publishing…' : 'Publish update'}
            </button>
          </div>
        </WorkflowModal>
      )}

      {modal === 'publish-gate' && (
        <WorkflowModal title={`${contentKind === 'course' ? 'Course' : 'Lesson'} can’t be published yet`}>
          {/* referenced-lesson-packages gate: the course references each
              lesson's published package, so every lesson must be published at
              least once before the course can be. Mirrors the Lesson Complete
              validation panel (named lessons + Review lessons navigation).
              In the Emtrain context the primary action publishes the listed
              lessons explicitly and resumes the interrupted flow — the server
              never cascades. Client contexts stay block-only: base lesson
              state must not move on a client workflow. */}
          <p style={styles.modalBody}>
            Each lesson must be published before this {contentNoun} can be published.
          </p>
          {!isClientContext && (
            <p style={styles.modalBody}>
              You can publish them from here: each lesson below is published exactly as it
              is now and locked, then the {contentNoun} is published.
            </p>
          )}
          <p style={styles.modalNote}>Not yet published:</p>
          <ul style={styles.lessonList}>
            {unpublishedLessonIds.map((lessonId) => {
              const outcome = gateOutcomes[lessonId];
              return (
                <li key={lessonId}>
                  {onNavigateToLesson ? (
                    <button type="button" style={styles.lessonLink} disabled={busy} onClick={() => { setModal(null); onNavigateToLesson(lessonId); }}>
                      {lessonName(lessonId)}
                    </button>
                  ) : (
                    lessonName(lessonId)
                  )}
                  {outcome?.status === 'published' && (
                    <span style={{ color: designTokens.colors.textSecondary }}> — published</span>
                  )}
                  {outcome?.status === 'failed' && (
                    <span style={{ color: designTokens.colors.errorText }}> — {outcome.reason}</span>
                  )}
                </li>
              );
            })}
          </ul>
          {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
            {onNavigateToLesson && unpublishedLessonIds[0] !== undefined && (
              <button
                type="button"
                style={isClientContext ? styles.primaryButton : styles.secondaryButton}
                disabled={busy}
                onClick={() => {
                  const target = unpublishedLessonIds.find((id) => gateOutcomes[id]?.status === 'failed') ?? unpublishedLessonIds[0];
                  setModal(null);
                  if (target) onNavigateToLesson(target);
                }}
              >
                Review lessons
              </button>
            )}
            {!isClientContext && (
              <button
                type="button"
                style={styles.primaryButton}
                disabled={busy}
                onClick={() => void executeGateLessonPublishes()}
              >
                {busy
                  ? busyLabel ?? 'Publishing…'
                  : `Publish ${unpublishedLessonIds.length === 1 ? 'lesson' : `${unpublishedLessonIds.length} lessons`} and continue`}
              </button>
            )}
          </div>
        </WorkflowModal>
      )}

      {modal === 'export-warning' && (
        <WorkflowModal title="Export last published version?">
          <p style={styles.modalBody}>
            You have unpublished edits. The SCORM package will use the last published version,
            not your current changes.
          </p>
          <p style={styles.modalBody}>To include your latest edits, publish an update first.</p>
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
            <button type="button" style={styles.primaryButton} onClick={() => setModal('export-confirm')} disabled={busy}>
              Export last published version
            </button>
          </div>
        </WorkflowModal>
      )}

      {modal === 'export-confirm' && (
        <WorkflowModal title="Export SCORM package?">
          {isClientContext ? (
            <>
              <p style={styles.modalBody}>This will download a SCORM .zip file scoped to this client account.</p>
              <p style={styles.modalBody}>Learner progress will be tracked in this client account’s LMS only.</p>
            </>
          ) : (
            <>
              <p style={styles.modalBody}>This will download a SCORM .zip file scoped to the Emtrain account.</p>
              <p style={styles.modalBody}>
                This file is for testing purposes only. Learner progress will not be tracked in
                any client account.
              </p>
              <p style={styles.modalNote}>To generate a client-scoped package, export from that client account.</p>
            </>
          )}
          {errorMessage && <p style={{ ...styles.modalNote, color: designTokens.colors.errorText }}>{errorMessage}</p>}
          <div style={styles.modalActions}>
            <button type="button" style={styles.secondaryButton} onClick={closeModal} disabled={busy}>Cancel</button>
            <button type="button" style={styles.primaryButton} onClick={() => void handleConfirmExport()} disabled={busy}>
              {busy ? busyLabel ?? 'Preparing SCORM .zip…' : 'Export SCORM .zip'}
            </button>
          </div>
        </WorkflowModal>
      )}

      {modal === 'deploy-results' && (
        <WorkflowModal title="Publish results">
          {/* Copy system: deployment is never author-facing vocabulary — the
              outcome speaks in terms of publishing/availability. Success is a
              single aggregate message; failures list per-account error details
              (failed accounts stay retryable via publish-update). "Published"
              is claimed at upload time, but DELIVERY is only ever claimed by
              the live badge below — never implied by this dialog. */}
          {failedDeploys.length === 0 ? (
            <p style={styles.modalBody}>
              {contentKind === 'course' ? 'Course' : 'Lesson'} published successfully. It is now
              available for download from any account it is assigned to.
            </p>
          ) : (
            <>
              <p style={styles.modalBody}>
                {contentKind === 'course' ? 'Course' : 'Lesson'} published, but some deliveries did
                not complete — the affected account contexts keep their previous package. Resolve the
                issues below, then publish an update to retry.
              </p>
              <p style={styles.modalBody}>Error details:</p>
              <div>
                {failedDeploys.map((result, index) => (
                  <div key={`${result.accountSlug}-${index}`} style={styles.resultRow}>
                    <span style={{ fontWeight: 600 }}>{result.accountSlug}</span>
                    <span style={{ color: designTokens.colors.textSecondary }}>
                      {result.error ?? 'Unknown error'}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
          {warningDeploys.length > 0 && (
            <>
              <p style={styles.modalBody}>
                <span style={{ color: designTokens.colors.workflowDraft, fontWeight: 600 }}>
                  ⚠ Delivery caveats
                </span>{' '}
                — these account contexts published, but delivery is degraded or unverified:
              </p>
              <div>
                {warningDeploys.map((result, index) => (
                  <div key={`warn-${result.accountSlug}-${index}`} style={styles.resultRow}>
                    <span style={{ fontWeight: 600 }}>{result.accountSlug}</span>
                    <span style={{ color: designTokens.colors.textSecondary }}>{result.warning}</span>
                  </div>
                ))}
              </div>
            </>
          )}
          {verifyTargets.length > 0 && deployScopeCount <= INLINE_VERIFY_MAX_SCOPES && (
            <>
              <p style={styles.modalBody}>
                Content delivery is verified below, per account scope — players show this publish
                once its scope is green.
              </p>
              <div>
                {verifyTargets.map((target) => (
                  <CdnLiveStatus
                    key={`verify-${target.accountSlug}`}
                    packageId={packageId}
                    accountSlug={target.accountSlug}
                    label={target.accountSlug === BASE_ACCOUNT_SLUG ? 'Standard (Emtrain)' : target.accountSlug}
                    expectedDeployId={target.deployId}
                    pollToken={publishState?.lastPublishedAt ?? null}
                    publishedAt={publishState?.lastPublishedAt ?? null}
                  />
                ))}
              </div>
            </>
          )}
          {(deployScopeCount > INLINE_VERIFY_MAX_SCOPES
            || (deployScopeCount === 0 && failedDeploys.length > 0)) && (
            <>
              <p style={styles.modalBody}>
                Delivery across account contexts is summarized below — the delivery status page
                tracks each context until it serves this publish.
              </p>
              <DeliverySummaryLine contentKind={contentKind} packageId={packageId} />
            </>
          )}
          <div style={styles.modalActions}>
            <button type="button" style={styles.primaryButton} onClick={closeModal}>OK</button>
          </div>
        </WorkflowModal>
      )}

      {errorMessage && modal === null && (
        <span role="alert" style={{ fontSize: '13px', color: designTokens.colors.errorText }}>{errorMessage}</span>
      )}
      {toast && (
        <span style={styles.toast} role="status" aria-live="polite">{toast}</span>
      )}
    </span>
  );
}
